import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
    List as MuiList,
    ListItem as MuiListItem,
    Typography,
    Box,
    IconButton, 
    Container,
    useTheme,
} from '@mui/material';
import useMediaQuery from '../useMediaquery';
import { data } from '../data';
import {useTranslation} from "react-i18next";

// Styled components with Apple-style theme
const StyledList = styled(MuiList)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridTemplateRows: 'repeat(2, auto)',
    gap: '24px',
    width: '100%',
    margin: 0,
    padding: '24px',
    listStyle: 'none',
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'repeat(4, auto)',
        padding: '16px',
        gap: '16px',
    },
    [theme.breakpoints.up('lg')]: {
        padding: '24px 120px',
    },
    [theme.breakpoints.up(1440)]: {
        padding: '24px calc(16px + 120px)',
    },
}));

const StyledListItem = styled(MuiListItem)(({ theme }) => ({
    listStyle: 'none',
    cursor: 'pointer',
    transition: 'all 400ms cubic-bezier(0.4, 0, 0.2, 1)',
    background: theme.palette.background.paper,
    borderRadius: '20px',
    border: `1px solid ${theme.palette.grey[200]}`,
    position: 'relative',
    zIndex: 1,
    boxShadow: `0 4px 16px ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'}`,
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: `0 12px 40px ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.15)'}`,
        borderColor: theme.palette.primary.main,
    },
    '&.open-item': {
        transform: 'translateY(-12px)',
        zIndex: 10,
        boxShadow: `0 20px 60px ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.2)'}`,
        borderColor: theme.palette.primary.main,
        '& .icon-container': {
            transform: 'rotate(180deg)',
        },
    },
    [theme.breakpoints.down('sm')]: {
        transition: 'transform 400ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms ease-in-out',
        '&:not(.open-item)': {
            opacity: 0.7,
        },
    },
}));

const ListItemContent = styled(Box)(({ theme }) => ({
    width: '100%',
    padding: '32px 24px',
    borderRadius: '20px',
    transition: 'all 0.4s ease-in-out',
    [theme.breakpoints.up('sm')]: {
        padding: '48px 54px',
    },
    '& .subtitle2': {
        fontSize: '0.875rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: theme.palette.text.secondary,
        marginBottom: '0.5rem',
        display: 'block',
    },
    '& .h3': {
        fontSize: '1.5rem',
        fontWeight: 700,
        lineHeight: 1.25,
        color: theme.palette.text.primary,
        margin: '0 0 1rem 0',
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.75rem',
        },
    },
}));

const ItemTextBlock = styled(Box)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.6,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    transition: 'opacity 0.3s ease-in-out, max-height 0.4s ease-in-out',
    maxHeight: 0,
    overflow: 'hidden',
    '&.open': {
        maxHeight: '500px',
        opacity: 1,
    },
    [theme.breakpoints.down('sm')]: {
        transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    },
}));

const Expanded = styled(Box)(({ theme }) => ({
    opacity: 1,
    transition: 'opacity 0.3s ease-in-out',
    '&.open': {
        opacity: 1,
    },
}));

const ItemTextList = styled(MuiList)(({ theme }) => ({
    margin: 0,
    padding: '16px 0 0 20px',
    listStyleType: 'disc',
    listStylePosition: 'inside',
    minHeight: '200px',
    '& li': {
        fontSize: '1rem',
        lineHeight: 1.4,
        marginBottom: '0.75rem',
        color: theme.palette.text.secondary,
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.125rem',
        },
    },
}));

const IconContainer = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '32px',
    right: '24px',
    transition: 'transform 200ms ease-in-out',
    color: theme.palette.primary.main,
    '& svg': {
        display: 'block',
        fill: theme.palette.primary.main,
    },
}));

const List = () => {
    const [selectedIndex, setSelectedIndex] = useState(null);
    const isMobile = useMediaQuery('(max-width: 899px)');
    const { t } = useTranslation();
    const theme = useTheme();

    const onSelect = (index, e) => {
        if (e.target.closest('.list-item') instanceof HTMLLIElement) {
            setSelectedIndex((prev) => (prev === index ? null : index));
        }
    };

    // Limit to 4 items for 2x2 grid
    const displayData = data.slice(0, 4);

    return (
        <Container sx={{
            justifyContent: 'center',
            alignItems: 'center',
        }}>
        <StyledList>
            {displayData.map(({ title, Text }, index) => (
                <StyledListItem
                    key={index}
                    className={`list-item ${selectedIndex === index ? 'open-item' : ''}`}
                    onClick={(e) => onSelect(index, e)}
                    sx={
                        selectedIndex === index
                            ? {
                                '& .list-item-content': {
                                    backgroundColor: theme.palette.primary.main + '10',
                                    borderColor: theme.palette.primary.main,
                                },
                            }
                            : {}
                    }
                >
                    <ListItemContent className="list-item-content">
                        {isMobile && (
                            <IconContainer className="icon-container">
                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M5.29243 11.2924C5.3853 11.1995 5.4956 11.1257 5.61699 11.0754C5.73839 11.0251 5.86852 10.9992 5.99993 10.9992C6.13135 10.9992 6.26148 11.0251 6.38287 11.0754C6.50427 11.1257 6.61456 11.1995 6.70743 11.2924L15.9999 20.5862L25.2924 11.2924C25.4801 11.1048 25.7346 10.9994 25.9999 10.9994C26.2653 10.9994 26.5198 11.1048 26.7074 11.2924C26.8951 11.4801 27.0005 11.7346 27.0005 11.9999C27.0005 12.2653 26.8951 12.5198 26.7074 12.7074L16.7074 22.7074C16.6146 22.8004 16.5043 22.8742 16.3829 22.9245C16.2615 22.9748 16.1313 23.0007 15.9999 23.0007C15.8685 23.0007 15.7384 22.9748 15.617 22.9245C15.4956 22.8742 15.3853 22.8004 15.2924 22.707bastian4L5.29243 12.7074C5.19946 12.6146 5.1257 12.5043 5.07537 12.3829C5.02505 12.2615 4.99915 12.1314 4.99915 11.9999C4.99915 11.8685 5.02505 11.7384 5.07537 11.617C5.1257 11.4956 5.19946 11.3853 5.29243 11.2924Z"
                                    />
                                </svg>
                            </IconContainer>
                        )}
                        <Typography className="subtitle2">{t('valuePropositionSection.prefix')}</Typography>
                        <Typography className="h3">{t(title)}</Typography>
                        <ItemTextBlock className={selectedIndex === index ? 'open' : ''}>
                            <Expanded className={selectedIndex === index ? 'open' : ''}>
                                <ItemTextList>{Text()}</ItemTextList>
                            </Expanded>
                        </ItemTextBlock>
                    </ListItemContent>
                </StyledListItem>
            ))}
        </StyledList>
        </Container>
    );
};

export default List;